import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PaywallSheet } from '../components/PaywallSheet.js';
import {
  ANGLE_INCREMENT_OPTIONS,
  ANGLE_UNIT_OPTIONS,
  BENDER_OPTIONS,
  CONDUIT_OPTIONS,
  FRACTION_PRECISION_OPTIONS,
  LENGTH_UNIT_OPTIONS,
  LEVEL_TOLERANCE_OPTIONS,
  formatDefaultConduitLabel,
  getConduitOptionsForDefaults,
  getMaterialOptionsForBender,
  setAngleIncrement,
  setAppSetting,
  useAppSettings,
  type AngleIncrement,
  type AngleUnit,
  type DefaultBender,
  type DefaultConduit,
  type DefaultMaterial,
  type FractionPrecision,
  type LengthUnit,
  type LevelTolerance,
} from '../lib/app-settings.js';
import {
  getBenderDataQualitySummary,
  type BenderDataQualitySummary,
} from '../lib/bender-verification.js';
import {
  canStartPurchaseFlow,
  restorePurchases,
  setSubscriptionTier,
  statusMessageFor,
  useSubscription,
  type SubscriptionActionStatus,
} from '../lib/subscription.js';
import {
  WEB_APP_INFO,
  formatInstalledAppVersion,
  getInstalledAppInfo,
  isNative,
  type InstalledAppInfo,
} from '../lib/platform.js';
import { LEGAL_LINKS } from '../lib/legal-links.js';

interface UpgradeFeature {
  feature: string;
  continueTo?: string;
}

export default function SettingsScreen() {
  const navigate = useNavigate();
  const { tier, isPro } = useSubscription();
  const [upgradeFeature, setUpgradeFeature] = useState<UpgradeFeature | null>(null);
  const [restoreStatus, setRestoreStatus] = useState<SubscriptionActionStatus | null>(null);
  const [appInfo, setAppInfo] = useState<InstalledAppInfo>(WEB_APP_INFO);
  const purchasesAvailable = canStartPurchaseFlow();
  const settings = useAppSettings();
  const materialOptions = getMaterialOptionsForBender(settings.defaultBender);
  const conduitOptions = getConduitOptionsForDefaults(
    settings.defaultBender,
    settings.defaultMaterial,
  );
  const dataQuality = getBenderDataQualitySummary();

  useEffect(() => {
    if (!isNative()) return;

    let active = true;
    void getInstalledAppInfo().then((info) => {
      if (active) setAppInfo(info);
    });
    return () => {
      active = false;
    };
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      <header>
        <h1 style={{ fontSize: 'var(--text-3xl)', marginBottom: 'var(--space-1)' }}>Settings</h1>
      </header>

      <ProfileCard
        tier={tier}
        subtitle={`${formatDefaultConduitLabel(
          settings.defaultConduit,
          settings.defaultMaterial,
        )} \u00b7 ${settings.defaultBender}`}
      />

      <SettingsGroup label="Truebend Pro">
        <ProSettingsPanel
          isPro={isPro}
          purchasesAvailable={purchasesAvailable}
          restoreStatus={restoreStatus}
          onOpenUpgrade={() => {
            setRestoreStatus(null);
            setUpgradeFeature({ feature: 'advanced calculators' });
          }}
          onRestore={() => {
            if (!purchasesAvailable) {
              setRestoreStatus('unavailable');
              return;
            }
            // Must catch: an escaping rejection reaches the global
            // 'unhandledrejection' handler in lib/startup-recovery.ts, which replaces the
            // whole document with a full-screen "Truebend needs a restart" alert.
            void restorePurchases()
              .then((result) => setRestoreStatus(result.status))
              .catch(() => setRestoreStatus('unavailable'));
          }}
        />
      </SettingsGroup>

      <SettingsGroup label="Tools">
        <button
          type="button"
          aria-label={isPro ? 'Open degree level' : 'Open degree level (Pro)'}
          onClick={() => {
            if (isPro) {
              navigate('/level?returnTo=/settings');
              return;
            }
            setRestoreStatus(null);
            setUpgradeFeature({ feature: 'degree level', continueTo: '/level?returnTo=/settings' });
          }}
          style={setupButtonStyle}
        >
          <span>
            <span style={{ display: 'block', fontWeight: 850 }}>Degree level</span>
            <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)' }}>
              Zero and read bend angle with the Pro level
            </span>
          </span>
          {!isPro ? (
            <span style={settingsProBadgeStyle}>Pro</span>
          ) : (
            <span aria-hidden="true" style={{ color: 'var(--color-accent)', fontWeight: 900 }}>
              &rsaquo;
            </span>
          )}
        </button>
        <button
          type="button"
          aria-label={isPro ? 'Open Square Align' : 'Open Square Align (Pro)'}
          onClick={() => {
            if (isPro) {
              navigate('/square-align?target=90&returnTo=/settings');
              return;
            }
            setRestoreStatus(null);
            setUpgradeFeature({
              feature: 'square align',
              continueTo: '/square-align?target=90&returnTo=/settings',
            });
          }}
          style={setupButtonStyle}
        >
          <span>
            <span style={{ display: 'block', fontWeight: 850 }}>Square Align</span>
            <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)' }}>
              Align conduit square to a building reference
            </span>
          </span>
          {!isPro ? (
            <span style={settingsProBadgeStyle}>Pro</span>
          ) : (
            <span aria-hidden="true" style={{ color: 'var(--color-accent)', fontWeight: 900 }}>
              &rsaquo;
            </span>
          )}
        </button>
      </SettingsGroup>

      <SettingsGroup label="Daily bender">
        <button
          type="button"
          onClick={() => navigate('/onboarding', { state: { returnTo: '/settings' } })}
          style={setupButtonStyle}
        >
          <span>
            <span style={{ display: 'block', fontWeight: 850 }}>Run quick setup</span>
            <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)' }}>
              Bender, conduit, material
            </span>
          </span>
          <span aria-hidden="true" style={{ color: 'var(--color-accent)', fontWeight: 900 }}>
            &rsaquo;
          </span>
        </button>
        <DataQualityNote summary={dataQuality} />
        <BenderPickList value={settings.defaultBender} />
      </SettingsGroup>

      <SettingsGroup label="Defaults">
        <SelectRow
          label="Default conduit"
          value={settings.defaultConduit}
          displayValue={formatDefaultConduitLabel(
            settings.defaultConduit,
            settings.defaultMaterial,
          )}
          options={conduitOptions.length > 0 ? conduitOptions : CONDUIT_OPTIONS}
          optionLabels={(conduitOptions.length > 0 ? conduitOptions : CONDUIT_OPTIONS).map(
            (option) => formatDefaultConduitLabel(option, settings.defaultMaterial),
          )}
          onChange={(value) => setAppSetting('defaultConduit', value as DefaultConduit)}
        />
        <SelectRow
          label="Default material"
          value={settings.defaultMaterial}
          options={materialOptions}
          onChange={(value) => setAppSetting('defaultMaterial', value as DefaultMaterial)}
        />
      </SettingsGroup>

      <SettingsGroup label="Units & precision">
        <SelectRow
          label="Length unit"
          value={settings.lengthUnit}
          options={LENGTH_UNIT_OPTIONS}
          onChange={(value) => setAppSetting('lengthUnit', value as LengthUnit)}
        />
        <SelectRow
          label="Display precision"
          value={settings.fractionPrecision}
          options={FRACTION_PRECISION_OPTIONS}
          onChange={(value) => setAppSetting('fractionPrecision', value as FractionPrecision)}
        />
        <SelectRow
          label="Angle unit"
          value={settings.angleUnit}
          options={ANGLE_UNIT_OPTIONS}
          onChange={(value) => setAppSetting('angleUnit', value as AngleUnit)}
        />
        <SelectRow
          label="Angle increment"
          value={String(settings.angleIncrement)}
          displayValue={formatAngle(settings.angleIncrement)}
          options={ANGLE_INCREMENT_OPTIONS.map(String)}
          optionLabels={ANGLE_INCREMENT_OPTIONS.map(formatAngle)}
          onChange={(value) => setAngleIncrement(Number(value) as AngleIncrement)}
        />
        <SelectRow
          label="Level tolerance"
          value={String(settings.levelTolerance)}
          displayValue={formatAngle(settings.levelTolerance)}
          options={LEVEL_TOLERANCE_OPTIONS.map(String)}
          optionLabels={LEVEL_TOLERANCE_OPTIONS.map(formatAngle)}
          onChange={(value) => setAppSetting('levelTolerance', Number(value) as LevelTolerance)}
        />
        <SelectRow
          label="Nudge step"
          value={settings.roundToNearest}
          options={FRACTION_PRECISION_OPTIONS}
          onChange={(value) => setAppSetting('roundToNearest', value as FractionPrecision)}
        />
      </SettingsGroup>

      <SettingsGroup label="Appearance">
        <ToggleRow
          label="Dark mode"
          caption="Recommended on jobsite"
          checked={settings.darkMode}
          onChange={(checked) => setAppSetting('darkMode', checked)}
        />
        <ToggleRow
          label="High-contrast text"
          checked={settings.highContrastText}
          onChange={(checked) => setAppSetting('highContrastText', checked)}
        />
        <ToggleRow
          label="Haptic feedback"
          checked={settings.hapticFeedback}
          onChange={(checked) => setAppSetting('hapticFeedback', checked)}
        />
      </SettingsGroup>

      <SettingsGroup label="About">
        <div style={aboutVersionRowStyle}>
          <span>Version</span>
          <span className="mono">{formatInstalledAppVersion(appInfo)}</span>
        </div>
        <div style={aboutLinksStyle}>
          Truebend by{' '}
          <a href="https://509electric.com" target="_blank" rel="noreferrer">
            509 Electric
          </a>
          {' \u00b7 '}
          <a href={LEGAL_LINKS.support} target="_blank" rel="noreferrer">
            Support
          </a>
          {' \u00b7 '}
          <a href={LEGAL_LINKS.privacy} target="_blank" rel="noreferrer">
            Privacy
          </a>
          {' \u00b7 '}
          <a href={LEGAL_LINKS.terms} target="_blank" rel="noreferrer">
            Terms of Use
          </a>
        </div>
      </SettingsGroup>

      {import.meta.env.VITE_ENABLE_MOCK_PURCHASES === 'true' && (
        <SettingsGroup label="Testing (internal build only)">
          <button type="button" onClick={() => setSubscriptionTier('pro')} style={setupButtonStyle}>
            <span>
              <span style={{ display: 'block', fontWeight: 850 }}>Unlock Pro (test)</span>
              <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)' }}>
                Mock unlock — never present in App Store builds
              </span>
            </span>
            <span aria-hidden="true" style={{ color: 'var(--color-accent)', fontWeight: 900 }}>
              &rsaquo;
            </span>
          </button>
          <button
            type="button"
            onClick={() => setSubscriptionTier('free')}
            style={setupButtonStyle}
          >
            <span style={{ display: 'block', fontWeight: 850 }}>Reset to Free</span>
          </button>
        </SettingsGroup>
      )}

      {upgradeFeature && (
        <PaywallSheet
          feature={upgradeFeature.feature}
          onClose={() => setUpgradeFeature(null)}
          onUpgrade={() => {
            const continueTo = upgradeFeature.continueTo;
            setUpgradeFeature(null);
            if (continueTo) {
              navigate(continueTo);
            }
          }}
        />
      )}
    </div>
  );
}

function ProfileCard({ tier, subtitle }: { tier: string; subtitle: string }) {
  return (
    <section
      style={{
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-4)',
        display: 'grid',
        gridTemplateColumns: '48px 1fr auto',
        alignItems: 'center',
        gap: 'var(--space-3)',
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          background: 'var(--color-accent)',
          color: 'var(--color-text-on-orange)',
          display: 'grid',
          placeItems: 'center',
          fontWeight: 900,
        }}
      >
        TB
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 'var(--text-lg)', fontWeight: 850 }}>Truebend</div>
        <div
          style={{
            color: 'var(--color-text-muted)',
            fontSize: 'var(--text-sm)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
          }}
        >
          {subtitle}
        </div>
      </div>
      <div
        style={{
          background: 'rgba(249, 115, 22, 0.16)',
          color: 'var(--color-accent)',
          borderRadius: 'var(--radius-pill)',
          padding: 'var(--space-1) var(--space-2)',
          fontSize: 'var(--text-xs)',
          fontWeight: 900,
          textTransform: 'uppercase',
        }}
      >
        {tier}
      </div>
    </section>
  );
}

function DataQualityNote({ summary }: { summary: BenderDataQualitySummary }) {
  const [expanded, setExpanded] = useState(false);
  const hasIssues = summary.issueLabels.length > 0;

  return (
    <div style={dataQualityStyle}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 'var(--space-2)',
        }}
      >
        <span
          style={{
            color: 'var(--color-text-primary)',
            fontWeight: 850,
            whiteSpace: 'nowrap',
          }}
        >
          Data quality
        </span>
        <span
          style={summary.hasFieldReferences ? dataQualityWarningBadgeStyle : dataQualityBadgeStyle}
        >
          {summary.label}
        </span>
      </div>
      <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)' }}>
        {summary.caption}
      </div>
      {hasIssues && (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded((current) => !current)}
          style={dataQualityAuditToggleStyle}
        >
          {expanded ? 'Hide bender data checks' : 'Show bender data checks'}
        </button>
      )}
      {hasIssues && expanded && (
        <div style={dataQualityAuditStyle}>
          <div style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-xs)' }}>
            {summary.issueCountLabel}
          </div>
          <ul style={dataQualityIssueListStyle}>
            {summary.issueLabels.map((issueLabel) => (
              <li key={issueLabel}>{issueLabel}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function SettingsGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section>
      <SectionLabel>{label}</SectionLabel>
      <div style={groupCardStyle}>{children}</div>
    </section>
  );
}

function ProSettingsPanel({
  isPro,
  purchasesAvailable,
  restoreStatus,
  onOpenUpgrade,
  onRestore,
}: {
  isPro: boolean;
  purchasesAvailable: boolean;
  restoreStatus: SubscriptionActionStatus | null;
  onOpenUpgrade: () => void;
  onRestore: () => void;
}) {
  // Only report what actually happened. Previously this defaulted to 'unavailable'
  // whenever purchases weren't available, which rendered a failure message before the
  // customer had done anything — the disabled button below already says as much.
  const restoreMessage = restoreStatus ? statusMessageFor(restoreStatus) : '';

  return (
    <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
      {isPro ? (
        <div style={proStatusStyle}>
          <span style={{ display: 'grid', gap: 2 }}>
            <span style={{ color: 'var(--color-text-primary)', fontWeight: 850 }}>Pro active</span>
            <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)' }}>
              Unlocked — one-time purchase
            </span>
          </span>
          <span style={proActiveBadgeStyle}>Active</span>
        </div>
      ) : (
        <button
          type="button"
          aria-label="Unlock advanced calculators"
          onClick={onOpenUpgrade}
          style={proActionStyle}
        >
          <span style={{ display: 'grid', gap: 2, minWidth: 0, textAlign: 'left' }}>
            <span style={{ color: 'var(--color-text-primary)', fontWeight: 850 }}>
              Advanced calculators
            </span>
            <span
              style={{
                color: 'var(--color-text-muted)',
                fontSize: 'var(--text-xs)',
                lineHeight: 1.3,
              }}
            >
              Degree level, Square Align, back-to-back, 4-point saddle, kick, rolling, parallel,
              concentric, segmented
            </span>
          </span>
          <span style={proUnlockBadgeStyle}>Unlock</span>
        </button>
      )}
      <button
        type="button"
        disabled={!purchasesAvailable}
        onClick={onRestore}
        style={purchasesAvailable ? restorePurchaseStyle : restorePurchaseDisabledStyle}
      >
        {purchasesAvailable ? 'Restore Purchases' : 'Restore unavailable'}
      </button>
      {restoreMessage && (
        <div role="status" aria-live="polite" style={restoreStatusStyle}>
          {restoreMessage}
        </div>
      )}
    </div>
  );
}

function BenderPickList({ value }: { value: DefaultBender }) {
  return (
    <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
      {BENDER_OPTIONS.map((option) => {
        const active = option === value;
        return (
          <button
            key={option}
            type="button"
            aria-pressed={active}
            onClick={() => setAppSetting('defaultBender', option)}
            style={{
              background: active ? 'rgba(249, 115, 22, 0.08)' : 'var(--color-bg)',
              border: `1px solid ${active ? 'var(--color-accent)' : 'var(--color-border)'}`,
              borderRadius: 'var(--radius-md)',
              color: 'var(--color-text-primary)',
              padding: 'var(--space-3)',
              display: 'grid',
              gridTemplateColumns: '28px 1fr auto',
              alignItems: 'center',
              gap: 'var(--space-3)',
              textAlign: 'left',
              width: '100%',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                border: `2px solid ${active ? 'var(--color-accent)' : 'var(--color-text-muted)'}`,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              {active && (
                <span
                  style={{
                    width: 9,
                    height: 9,
                    borderRadius: '50%',
                    background: 'var(--color-accent)',
                  }}
                />
              )}
            </span>
            <span style={{ fontWeight: 850 }}>{option}</span>
            {active && (
              <span
                style={{
                  color: 'var(--color-accent)',
                  fontSize: 'var(--text-xs)',
                  fontWeight: 900,
                  textTransform: 'uppercase',
                }}
              >
                Your pick
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function SelectRow<T extends string>({
  label,
  value,
  displayValue,
  options,
  optionLabels,
  onChange,
}: {
  label: string;
  value: T;
  displayValue?: string;
  options: readonly T[];
  optionLabels?: readonly string[];
  onChange: (value: T) => void;
}) {
  return (
    <label style={rowStyle}>
      <span style={{ color: 'var(--color-text-primary)', fontWeight: 750 }}>{label}</span>
      <span
        style={{
          position: 'relative',
          color: 'var(--color-text-muted)',
          fontSize: 'var(--text-sm)',
          paddingRight: 'var(--space-4)',
        }}
      >
        {displayValue ?? value}
        <span aria-hidden="true" style={{ position: 'absolute', right: 0 }}>
          &rsaquo;
        </span>
        <select
          aria-label={label}
          value={value}
          onChange={(event) => onChange(event.target.value as T)}
          style={{
            position: 'absolute',
            inset: 0,
            opacity: 0,
            cursor: 'pointer',
          }}
        >
          {options.map((option, index) => (
            <option key={option} value={option}>
              {optionLabels?.[index] ?? option}
            </option>
          ))}
        </select>
      </span>
    </label>
  );
}

function ToggleRow({
  label,
  caption,
  checked,
  onChange,
}: {
  label: string;
  caption?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={checked}
      onClick={() => onChange(!checked)}
      style={{ ...rowStyle, width: '100%', color: 'var(--color-text-primary)' }}
    >
      <span style={{ display: 'grid', gap: '2px', textAlign: 'left' }}>
        <span style={{ fontWeight: 750 }}>{label}</span>
        {caption && (
          <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)' }}>
            {caption}
          </span>
        )}
      </span>
      <span
        aria-hidden="true"
        style={{
          width: 46,
          height: 28,
          borderRadius: 'var(--radius-pill)',
          background: checked ? 'var(--color-accent)' : 'var(--color-border)',
          border: `1px solid ${checked ? 'var(--color-accent)' : 'var(--color-border)'}`,
          padding: 3,
          display: 'flex',
          justifyContent: checked ? 'flex-end' : 'flex-start',
        }}
      >
        <span
          style={{
            width: 20,
            height: 20,
            borderRadius: '50%',
            background: '#fff',
            display: 'block',
          }}
        />
      </span>
    </button>
  );
}

const groupCardStyle: React.CSSProperties = {
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--space-2)',
};

const rowStyle: React.CSSProperties = {
  minHeight: '52px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--space-3)',
  padding: 'var(--space-3)',
  borderRadius: 'var(--radius-sm)',
  background: 'transparent',
};

const setupButtonStyle: React.CSSProperties = {
  ...rowStyle,
  width: '100%',
  color: 'var(--color-text-primary)',
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  marginBottom: 'var(--space-2)',
};

const settingsProBadgeStyle: React.CSSProperties = {
  color: 'var(--color-accent)',
  background: 'rgba(249, 115, 22, 0.14)',
  border: '1px solid rgba(249, 115, 22, 0.24)',
  borderRadius: 'var(--radius-pill)',
  padding: 'var(--space-1) var(--space-3)',
  fontSize: 'var(--text-xs)',
  fontWeight: 900,
  textTransform: 'uppercase',
};

const dataQualityStyle: React.CSSProperties = {
  display: 'grid',
  gap: 'var(--space-1)',
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--space-3)',
  marginBottom: 'var(--space-2)',
};

const dataQualityBadgeStyle: React.CSSProperties = {
  color: 'var(--color-blue)',
  background: 'var(--color-blue-soft)',
  border: '1px solid rgba(0, 122, 255, 0.28)',
  borderRadius: 'var(--radius-pill)',
  padding: 'var(--space-1) var(--space-2)',
  fontSize: '0.68rem',
  lineHeight: 1.2,
  fontWeight: 900,
  maxWidth: '100%',
  whiteSpace: 'normal',
};

const dataQualityWarningBadgeStyle: React.CSSProperties = {
  ...dataQualityBadgeStyle,
  color: 'var(--color-accent)',
  background: 'rgba(249, 115, 22, 0.12)',
  border: '1px solid rgba(249, 115, 22, 0.26)',
};

const dataQualityAuditStyle: React.CSSProperties = {
  display: 'grid',
  gap: 'var(--space-2)',
  paddingTop: 'var(--space-2)',
  borderTop: '1px solid var(--color-border)',
};

const dataQualityAuditToggleStyle: React.CSSProperties = {
  width: 'fit-content',
  color: 'var(--color-accent)',
  background: 'transparent',
  border: 0,
  borderRadius: 'var(--radius-sm)',
  padding: 'var(--space-1) 0',
  fontSize: 'var(--text-xs)',
  fontWeight: 900,
};

const dataQualityIssueListStyle: React.CSSProperties = {
  display: 'grid',
  gap: 'var(--space-1)',
  margin: 0,
  paddingLeft: '1.1rem',
  color: 'var(--color-text-muted)',
  fontSize: 'var(--text-xs)',
};

const aboutVersionRowStyle: React.CSSProperties = {
  ...rowStyle,
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  color: 'var(--color-text-primary)',
  fontSize: 'var(--text-sm)',
  fontWeight: 850,
  marginBottom: 'var(--space-2)',
};

const aboutLinksStyle: React.CSSProperties = {
  color: 'var(--color-text-secondary)',
  fontSize: 'var(--text-sm)',
  padding: 'var(--space-2) var(--space-3)',
};

const proStatusStyle: React.CSSProperties = {
  ...rowStyle,
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
};

const proActionStyle: React.CSSProperties = {
  ...rowStyle,
  width: '100%',
  color: 'var(--color-text-primary)',
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
};

const proActiveBadgeStyle: React.CSSProperties = {
  color: 'white',
  background: 'var(--color-blue)',
  borderRadius: 'var(--radius-pill)',
  padding: 'var(--space-1) var(--space-3)',
  fontSize: 'var(--text-xs)',
  fontWeight: 900,
};

const proUnlockBadgeStyle: React.CSSProperties = {
  color: 'var(--color-blue)',
  background: 'var(--color-blue-soft)',
  border: '1px solid rgba(0, 122, 255, 0.28)',
  borderRadius: 'var(--radius-pill)',
  padding: 'var(--space-1) var(--space-3)',
  fontSize: 'var(--text-xs)',
  fontWeight: 900,
};

const restorePurchaseStyle: React.CSSProperties = {
  width: '100%',
  minHeight: 46,
  color: 'var(--color-text-primary)',
  background: 'transparent',
  border: 0,
  borderRadius: 'var(--radius-sm)',
  padding: 'var(--space-3)',
  fontSize: 'var(--text-sm)',
  fontWeight: 850,
};

const restorePurchaseDisabledStyle: React.CSSProperties = {
  ...restorePurchaseStyle,
  color: 'var(--color-text-muted)',
  border: '1px solid var(--color-border)',
  background: 'var(--color-bg)',
  cursor: 'not-allowed',
};

const restoreStatusStyle: React.CSSProperties = {
  color: 'var(--color-text-secondary)',
  fontSize: 'var(--text-xs)',
  fontWeight: 800,
  textAlign: 'center',
  padding: '0 var(--space-3) var(--space-2)',
};

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 'var(--text-xs)',
        color: 'var(--color-text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        marginBottom: 'var(--space-2)',
        fontWeight: 850,
      }}
    >
      {children}
    </div>
  );
}

function formatAngle(angle: number): string {
  return `${Number.isInteger(angle) ? angle.toFixed(0) : angle.toFixed(1)}\u00b0`;
}
